// src/features/document/acknowledgment.ts
// Shared helpers for the acknowledgment (ознакомление) flow: status
// normalization (the API returns enum-prefixed values), actor extraction and
// progress stats.

export type AckStatus = 'PENDING' | 'ACKNOWLEDGED' | 'OVERDUE' | 'WAIVED';

export function normalizeAckStatus(raw: unknown): AckStatus {
  const s = String(raw ?? '').replace(/^ACKNOWLEDGMENT_STATUS_/, '');
  if (s === 'ACKNOWLEDGED' || s === 'OVERDUE' || s === 'WAIVED') return s;
  return 'PENDING';
}

export function ackActorId(ack: any): string {
  return ack?.actor?.entity?.id ?? ack?.actor?.id ?? ack?.user_id ?? ack?.user?.id ?? '';
}

export function ackActorName(ack: any): string {
  return (
    ack?.actor?.display_name ||
    ack?.user?.display_name ||
    ack?.user?.name ||
    ack?.name ||
    ''
  );
}

export function ackId(ack: any): string {
  return ack?.entity?.id ?? ack?.id ?? '';
}

export function parseDocumentVersion(value: unknown): number {
  const match = String(value ?? '').match(/\d+/);
  const version = match ? Number(match[0]) : 0;
  return Number.isFinite(version) && version > 0 ? Math.trunc(version) : 0;
}

export function acknowledgmentVersion(ack: any): number {
  return parseDocumentVersion(ack?.document_version);
}

/** One current state per person and document version. */
export function currentAcknowledgments(acks: any[]): any[] {
  const byActorAndVersion = new Map<string, any>();
  for (const ack of acks ?? []) {
    const actor = ackActorId(ack);
    const identity = actor || ackId(ack);
    if (!identity) continue;
    const key = `${identity}:${acknowledgmentVersion(ack)}`;
    const previous = byActorAndVersion.get(key);
    if (!previous ||
      (normalizeAckStatus(ack.status) === 'ACKNOWLEDGED' && normalizeAckStatus(previous.status) !== 'ACKNOWLEDGED') ||
      (normalizeAckStatus(ack.status) === normalizeAckStatus(previous.status) &&
        String(ack.requested_at || '') > String(previous.requested_at || ''))) {
      byActorAndVersion.set(key, ack);
    }
  }
  return [...byActorAndVersion.values()];
}

export interface AcknowledgmentVersionGroup {
  version: number;
  items: any[];
}

export function groupAcknowledgmentsByVersion(acks: any[]): AcknowledgmentVersionGroup[] {
  const groups = new Map<number, any[]>();
  currentAcknowledgments(acks).forEach((ack) => {
    const version = acknowledgmentVersion(ack);
    groups.set(version, [...(groups.get(version) ?? []), ack]);
  });
  return [...groups.entries()]
    .map(([version, items]) => ({ version, items }))
    .sort((a, b) => b.version - a.version);
}

export function findPendingForUser(acks: any[], userId: string, version?: number): any | null {
  if (!userId) return null;
  return currentAcknowledgments(acks)
    .filter((ack) =>
      ackActorId(ack) === userId &&
      normalizeAckStatus(ack.status) === 'PENDING' &&
      (version === undefined || acknowledgmentVersion(ack) === version))
    .sort((a, b) =>
      acknowledgmentVersion(b) - acknowledgmentVersion(a) ||
      String(b.requested_at || '').localeCompare(String(a.requested_at || '')))[0] ?? null;
}

export interface AckStats {
  total: number;
  pending: number;
  acknowledged: number;
  overdue: number;
  waived: number;
}

export function computeAckStats(acks: any[]): AckStats {
  const stats: AckStats = { total: 0, pending: 0, acknowledged: 0, overdue: 0, waived: 0 };
  for (const a of acks ?? []) {
    stats.total += 1;
    stats[normalizeAckStatus(a?.status).toLowerCase() as 'pending' | 'acknowledged' | 'overdue' | 'waived'] += 1;
  }
  return stats;
}
